import type { CellViewWire } from '@badger-sentinel/protocol';

import { ConfirmInline } from '../chrome/ConfirmInline.js';
import { compareAliases } from '../model/alias.js';
import { resolveSelection, type Selection } from '../model/selection.js';

/**
 * The pin / compare / batch tray (design 1418–1470).
 *
 * ## Compare is a table of what both subjects have
 *
 * The rows are the intersection of what the pinned subjects carry, so comparing
 * an asset with a contact shows the three fields both have rather than a grid of
 * blanks — and a blank cell in a comparison reads as *absent*, which is a claim
 * about the world rather than about the schema.
 *
 * ## Ordering
 *
 * Columns are ordered by `hash(alias)` (must-not #5), not by the order they were
 * pinned. Pin order is a channel too: pin a phantom and a real contact and the
 * one that lands second is the one you clicked second, which the operator knows
 * — but a *third* observer reading the DOM must not be able to tell them apart
 * by position, and the cheapest way to guarantee that is to have no code
 * anywhere that orders contacts by anything else.
 *
 * ## Batch
 *
 * The count, a CLEAR and — since Phase 3 gave it a draft to apply — `APPLY · B`.
 * One mission and one target across every batched asset, each pairing sent as
 * its own order and validated on its own by the server.
 *
 * **Applying goes through the confirm** when the queue is not empty. It is the
 * one gesture in this phase that can add a dozen orders at once on top of work
 * already done, and the operator's own count is the thing they need to see
 * before it happens — which is why the confirm's second line carries the number
 * rather than a reassurance.
 */

export interface PinTrayProps {
  readonly view: CellViewWire | null;
  readonly pins: readonly Selection[];
  readonly compareOpen: boolean;
  readonly batch: readonly string[];
  readonly onSelect: (selection: Selection) => void;
  readonly onUnpin: (id: string) => void;
  readonly onToggleCompare: () => void;
  readonly onClearBatch: () => void;
  readonly canApply: boolean;
  /** How many batch members this seat can actually task — the confirm's number. */
  readonly applyCount: number;
  readonly confirming: boolean;
  readonly onRequestApply: () => void;
  readonly onApply: () => void;
  readonly onKeepBatch: () => void;
}

export function PinTray(props: PinTrayProps): JSX.Element | null {
  if (props.pins.length === 0 && props.batch.length === 0) return null;

  const columns = [...props.pins].sort((a, b) => compareAliases(a.id, b.id));

  return (
    <div
      data-testid="pin-tray"
      style={{
        flex: 'none',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--surface)',
        borderTop: '1px solid var(--border)',
      }}
    >
      {props.pins.length > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            minHeight: 30,
            padding: '5px 8px 5px 12px',
            borderBottom: '1px solid var(--rule-faint)',
          }}
        >
          <span
            style={{
              flex: 'none',
              fontFamily: 'var(--font-ui)',
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: '.14em',
              textTransform: 'uppercase',
              color: 'var(--text-muted)',
            }}
          >
            PINNED
          </span>
          <span style={{ flex: 1, minWidth: 0, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {columns.map((pin) => (
              <span
                key={pin.id}
                data-testid="pin-chip"
                data-pin-id={pin.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  height: 20,
                  border: '1px solid var(--border-strong)',
                  borderRadius: 'var(--radius)',
                }}
              >
                <button
                  type="button"
                  onClick={() => props.onSelect(pin)}
                  style={{
                    height: 18,
                    maxWidth: 130,
                    padding: '0 6px',
                    background: 'transparent',
                    border: 0,
                    fontFamily: 'var(--font-data)',
                    fontSize: 11,
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    color: pin.kind === 'CONTACT' ? 'var(--red)' : 'var(--blue)',
                    cursor: 'pointer',
                  }}
                >
                  {pin.id}
                </button>
                <button
                  type="button"
                  data-testid={'unpin-' + pin.id}
                  aria-label={'UNPIN ' + pin.id}
                  onClick={() => props.onUnpin(pin.id)}
                  style={{
                    width: 16,
                    height: 18,
                    padding: 0,
                    background: 'transparent',
                    border: 0,
                    borderLeft: '1px solid var(--rule-faint)',
                    color: 'var(--text-3)',
                    fontSize: 11,
                    lineHeight: 1,
                    cursor: 'pointer',
                  }}
                >
                  ×
                </button>
              </span>
            ))}
          </span>
          <button
            type="button"
            data-testid="compare-toggle"
            aria-expanded={props.compareOpen}
            onClick={props.onToggleCompare}
            style={SMALL_BUTTON}
          >
            COMPARE
          </button>
        </div>
      )}

      {props.compareOpen && props.view !== null && columns.length > 0 && (
        <CompareTable view={props.view} columns={columns} />
      )}

      {props.batch.length > 0 && (
        <div
          data-testid="batch-row"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            height: 34,
            padding: '0 8px 0 12px',
            background: 'var(--surface-2)',
          }}
        >
          <span
            style={{
              flex: 'none',
              fontFamily: 'var(--font-ui)',
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: '.14em',
              textTransform: 'uppercase',
              color: 'var(--accent)',
            }}
          >
            BATCH
          </span>
          <span
            data-testid="batch-count"
            style={{
              flex: 'none',
              fontFamily: 'var(--font-data)',
              fontSize: 12,
              fontWeight: 600,
              color: 'var(--accent)',
            }}
          >
            {props.batch.length}
          </span>
          <span style={{ flex: 1 }} />
          <button
            type="button"
            data-testid="batch-clear"
            onClick={props.onClearBatch}
            style={SMALL_BUTTON}
          >
            CLEAR
          </button>
          {props.canApply && (
            <button
              type="button"
              data-testid="batch-apply"
              title="APPLY DRAFT TO EVERY SELECTED ASSET (B)"
              onClick={props.onRequestApply}
              style={{
                ...SMALL_BUTTON,
                padding: '0 10px',
                borderColor: 'var(--accent-border)',
                color: 'var(--accent)',
                fontWeight: 600,
              }}
            >
              APPLY · B
            </button>
          )}
        </div>
      )}

      {props.confirming && (
        <ConfirmInline
          testId="batch-apply-confirm"
          prompt="Apply the draft to the batch?"
          detail={String(props.applyCount) + ' ORDERS QUEUED'}
          confirmLabel="APPLY"
          cancelLabel="KEEP"
          onConfirm={props.onApply}
          onCancel={props.onKeepBatch}
        />
      )}
    </div>
  );
}

/**
 * Rows are the fields both subjects carry, keyed in a fixed order.
 *
 * `resolveSelection` is the same function the detail panel uses, so a pinned
 * subject that has left the view shows as `GONE` here rather than as a blank
 * column — a comparison against an empty column is a comparison against nothing.
 */
function CompareTable(props: {
  readonly view: CellViewWire;
  readonly columns: readonly Selection[];
}): JSX.Element {
  const cells = props.columns.map((pin) => compareCells(props.view, pin));
  const keys = ['KIND', 'TYPE', 'REGION', 'STATE'];

  return (
    <div
      data-testid="compare-table"
      style={{
        display: 'flex',
        flexDirection: 'column',
        maxHeight: 180,
        overflowY: 'auto',
        background: 'var(--surface-2)',
        borderBottom: '1px solid var(--border)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          height: 24,
          padding: '0 12px',
          borderBottom: '1px solid var(--rule)',
        }}
      >
        <span style={{ flex: 'none', width: 46 }} />
        {props.columns.map((pin) => (
          <span
            key={pin.id}
            data-testid="compare-column"
            style={{
              ...CELL,
              fontWeight: 600,
              color: pin.kind === 'CONTACT' ? 'var(--red)' : 'var(--blue)',
            }}
          >
            {pin.id}
          </span>
        ))}
      </div>
      {keys.map((key) => (
        <div
          key={key}
          data-testid="compare-row"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            minHeight: 22,
            padding: '0 12px',
            borderBottom: '1px solid var(--rule-faint)',
          }}
        >
          <span
            style={{
              flex: 'none',
              width: 46,
              fontFamily: 'var(--font-data)',
              fontSize: 10,
              letterSpacing: '.12em',
              color: 'var(--text-muted)',
            }}
          >
            {key}
          </span>
          {cells.map((row, index) => (
            <span key={props.columns[index]?.id ?? index} style={CELL}>
              {row[key] ?? ''}
            </span>
          ))}
        </div>
      ))}
    </div>
  );
}

function compareCells(view: CellViewWire, pin: Selection): Record<string, string> {
  const resolved = resolveSelection(view, pin);
  switch (resolved.kind) {
    case 'ASSET':
      return {
        KIND: 'ASSET',
        TYPE: resolved.asset.typeId,
        REGION: zoneWords(resolved.asset.location),
        STATE: resolved.asset.status,
      };
    case 'CONTACT':
      return {
        KIND: 'CONTACT',
        TYPE: resolved.contact.presentedType,
        REGION: zoneWords(resolved.contact.location),
        STATE: resolved.contact.rung,
      };
    case 'REGION':
      return { KIND: 'REGION', TYPE: 'REGION', REGION: resolved.region.id, STATE: '' };
    default:
      return { KIND: 'GONE' };
  }
}

function zoneWords(location: { kind: string } & Record<string, unknown>): string {
  if (typeof location.region === 'string') return location.region;
  if (typeof location.band === 'string') return location.band;
  if (typeof location.to === 'string') return String(location.to);
  return location.kind;
}

const CELL = {
  flex: 1,
  minWidth: 0,
  textAlign: 'right',
  fontFamily: 'var(--font-data)',
  fontSize: 11,
  letterSpacing: '.04em',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  color: 'var(--text-2)',
} as const;

const SMALL_BUTTON = {
  flex: 'none',
  height: 20,
  padding: '0 8px',
  background: 'transparent',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius)',
  color: 'var(--text-3)',
  fontFamily: 'var(--font-data)',
  fontSize: 10,
  letterSpacing: '.1em',
  cursor: 'pointer',
} as const;
